/**
 * Currency Management Vuex Module
 * 货币管理 Vuex 模块
 *
 * Handles currency switching with:
 * - Per-venue currency persistence (each venue saves its own currency preference)
 * - Default currency fallback mechanism
 *
 * 处理货币切换，包括：
 * - 按餐厅持久化货币（每个餐厅保存自己的货币偏好）
 * - 默认货币回退机制
 *
 * Shares localStorage with language module via 'mobile_locale_prefs':
 * { [venueId]: { language: 'fr', currency: 'EUR' } }
 * 与语言模块共享 localStorage 的 'mobile_locale_prefs'。
 */

import {
  DEFAULT_CURRENCY,
  DEFAULT_EXCHANGE_RATES,
  DEFAULT_SUPPORTED_CURRENCIES,
  mergeCurrencySettings,
} from '@/config/defaultSettings'

// Import shared locale prefs helpers from language module
// 从语言模块导入共享的地区偏好辅助函数
import { getLocalePrefs, saveLocalePrefs } from './language'

/**
 * Get saved currency for a specific venue
 * 获取特定餐厅的已保存货币
 *
 * @param {string} venueId - Venue ID / 餐厅 ID
 * @returns {string|null} - Saved currency code or null / 已保存的货币代码或 null
 */
function getSavedCurrency(venueId) {
  if (!venueId) return null
  const prefs = getLocalePrefs()
  return prefs[venueId]?.currency || null
}

export default {
  namespaced: true,

  state: {
    defaultCurrency: DEFAULT_CURRENCY, // Default currency for fallback / 默认货币（回退用）
    currentCurrency: DEFAULT_CURRENCY, // Currently selected currency / 当前选中的货币
    supportedCurrencies: DEFAULT_SUPPORTED_CURRENCIES, // Supported currencies list / 支持的货币列表
    exchangeRates: DEFAULT_EXCHANGE_RATES, // Exchange rates relative to default currency / 相对于默认货币的汇率
  },

  mutations: {
    /**
     * Set current currency
     * 设置当前货币
     */
    setCurrentCurrency(state, currency) {
      // Defensive check: ensure supportedCurrencies is an array
      // 防御性检查：确保 supportedCurrencies 是数组
      const supportedCurrencies = Array.isArray(state.supportedCurrencies) ? state.supportedCurrencies : DEFAULT_SUPPORTED_CURRENCIES
      const defaultCurrency = state.defaultCurrency || DEFAULT_CURRENCY

      // Validate if currency is in supported list, otherwise use default
      // 验证货币是否在支持列表中，否则使用默认货币
      const isSupported = supportedCurrencies.length === 0 || supportedCurrencies.some(c => c.code === currency)
      state.currentCurrency = isSupported ? currency : defaultCurrency
    },

    /**
     * Set supported currencies list
     * 设置支持的货币列表
     */
    setSupportedCurrencies(state, currencies) {
      state.supportedCurrencies = currencies
      // Defensive check: ensure currentCurrency exists
      // 防御性检查：确保 currentCurrency 存在
      const currentCurrency = state.currentCurrency || DEFAULT_CURRENCY
      const defaultCurrency = state.defaultCurrency || DEFAULT_CURRENCY

      // If current currency is not in supported list, reset to default
      // 如果当前货币不在支持列表中，重置为默认货币
      if (currencies.length > 0 && !currencies.some(c => c.code === currentCurrency)) {
        state.currentCurrency = defaultCurrency
      }
    },

    /**
     * Set exchange rates
     * 设置汇率
     */
    setExchangeRates(state, rates) {
      state.exchangeRates = rates
    },

    /**
     * Set default currency
     * 设置默认货币
     */
    setDefaultCurrency(state, currency) {
      state.defaultCurrency = currency
    },

    /**
     * Reset currency state (used after order completion or venue change)
     * 重置货币状态（用于订单完成后或切换餐厅时重置）
     */
    resetCurrency(state) {
      state.currentCurrency = state.defaultCurrency || DEFAULT_CURRENCY
    },

    /**
     * Reset to default settings
     * 重置为默认设置
     */
    resetToDefaults(state) {
      state.defaultCurrency = DEFAULT_CURRENCY
      state.currentCurrency = DEFAULT_CURRENCY
      state.supportedCurrencies = DEFAULT_SUPPORTED_CURRENCIES
      state.exchangeRates = DEFAULT_EXCHANGE_RATES
    },
  },

  getters: {
    /**
     * Get current currency info
     * 获取当前货币信息
     *
     * @returns {Object} Current currency object / 当前货币对象
     */
    currentCurrencyInfo: state => {
      // Defensive check: ensure supportedCurrencies is an array
      // 防御性检查：确保 supportedCurrencies 是数组
      const supportedCurrencies = Array.isArray(state.supportedCurrencies) ? state.supportedCurrencies : DEFAULT_SUPPORTED_CURRENCIES
      const currentCurrency = state.currentCurrency || DEFAULT_CURRENCY

      const currency = supportedCurrencies.find(c => c.code === currentCurrency)
      return (
        currency || {
          code: currentCurrency,
          symbol: '$',
          name: 'Australian Dollar',
          flag: '🇦🇺',
          symbolPosition: 'before',
        }
      )
    },

    /**
     * Get currency symbol
     * 获取货币符号
     *
     * @returns {string} Currency symbol / 货币符号
     */
    currencySymbol: (state, getters) => {
      return getters.currentCurrencyInfo.symbol || '$'
    },

    /**
     * Get symbol position for current currency
     * 获取当前货币的符号位置
     *
     * @returns {string} 'before' or 'after' / 'before' 或 'after'
     */
    symbolPosition: (state, getters) => {
      return getters.currentCurrencyInfo.symbolPosition || 'before'
    },

    /**
     * Get decimal separator for current currency
     * 获取当前货币的小数点分隔符
     *
     * @returns {string} '.' or ',' / '.' 或 ','
     */
    decimalSeparator: (state, getters) => {
      return getters.currentCurrencyInfo.decimalSeparator || '.'
    },

    /**
     * Convert price from venue's base currency (defaultCurrency) to display currency (currentCurrency)
     * 将价格从餐厅的基础货币（defaultCurrency）转换为显示货币（currentCurrency）
     *
     * @param {number} price - Price in venue's base currency / 餐厅基础货币的价格
     * @returns {number} - Price in current display currency / 当前显示货币的价格
     */
    convertPrice: state => price => {
      // Defensive checks / 防御性检查
      const currentCurrency = state.currentCurrency || DEFAULT_CURRENCY
      const defaultCurrency = state.defaultCurrency || DEFAULT_CURRENCY
      const exchangeRates = state.exchangeRates || DEFAULT_EXCHANGE_RATES

      // If displaying in the same currency as venue's base currency, no conversion needed
      // 如果显示货币与餐厅基础货币相同，无需转换
      if (currentCurrency === defaultCurrency) {
        return price
      }
      // Get the exchange rate for the target currency (relative to defaultCurrency)
      // 获取目标货币的汇率（相对于 defaultCurrency）
      const rate = exchangeRates[currentCurrency] || 1
      return price * rate
    },

    /**
     * Check if currency conversion is needed
     * 检查是否需要货币转换
     *
     * @returns {boolean} - True if current currency differs from default
     */
    needsConversion: state => {
      const currentCurrency = state.currentCurrency || DEFAULT_CURRENCY
      const defaultCurrency = state.defaultCurrency || DEFAULT_CURRENCY
      return currentCurrency !== defaultCurrency
    },

    /**
     * Format price with currency symbol
     * 格式化价格（带货币符号）
     *
     * @param {number} price - Price value / 价格值
     * @returns {string} - Formatted price string / 格式化后的价格字符串
     */
    formatPrice: (state, getters) => price => {
      const convertedPrice = getters.convertPrice(price)
      const symbol = getters.currencySymbol
      const position = getters.symbolPosition
      const separator = getters.decimalSeparator
      let formattedNumber = convertedPrice.toFixed(2)
      if (separator === ',') {
        formattedNumber = formattedNumber.replace('.', ',')
      }

      if (position === 'after') {
        return `${formattedNumber} ${symbol}`
      }
      return `${symbol}${formattedNumber}`
    },

    // Current currency getter / 当前货币 getter
    currentCurrency: state => state.currentCurrency || DEFAULT_CURRENCY,

    // Default currency getter / 默认货币 getter
    defaultCurrency: state => state.defaultCurrency || DEFAULT_CURRENCY,

    // Default currency info (for order receipt pages that show venue's base currency)
    // 默认货币信息（用于显示餐厅基础货币的订单收据页面）
    defaultCurrencyInfo: state => {
      const supportedCurrencies = Array.isArray(state.supportedCurrencies) ? state.supportedCurrencies : DEFAULT_SUPPORTED_CURRENCIES
      const defaultCurrency = state.defaultCurrency || DEFAULT_CURRENCY
      const currency = supportedCurrencies.find(c => c.code === defaultCurrency)
      return currency || { code: defaultCurrency, symbol: '$', name: 'Australian Dollar', symbolPosition: 'before' }
    },

    // Default currency symbol / 默认货币符号
    defaultCurrencySymbol: (state, getters) => {
      return getters.defaultCurrencyInfo.symbol || '$'
    },

    // Default currency symbol position / 默认货币符号位置
    defaultSymbolPosition: (state, getters) => {
      return getters.defaultCurrencyInfo.symbolPosition || 'before'
    },

    // Supported currencies getter / 支持的货币列表 getter
    currencies: state => Array.isArray(state.supportedCurrencies) ? state.supportedCurrencies : DEFAULT_SUPPORTED_CURRENCIES,

    // Check if multiple currencies are supported / 检查是否有多货币支持
    hasMultipleCurrencies: state => {
      const supportedCurrencies = Array.isArray(state.supportedCurrencies) ? state.supportedCurrencies : DEFAULT_SUPPORTED_CURRENCIES
      return supportedCurrencies.length > 1
    },
  },

  actions: {
    /**
     * Initialize currency for a specific venue
     * 为特定餐厅初始化货币
     *
     * Priority / 优先级:
     * 1. Per-venue saved preference from localStorage
     *    按餐厅保存的偏好（localStorage）
     * 2. Venue's default currency
     *    餐厅的默认货币
     *
     * @param {string} venueId - Venue ID / 餐厅 ID
     */
    initCurrency({ commit, state }, venueId) {
      // 1. Check per-venue saved preference / 检查按餐厅保存的偏好
      const savedCurrency = getSavedCurrency(venueId)
      if (savedCurrency) {
        // Defensive check: ensure supportedCurrencies is an array
        // 防御性检查：确保 supportedCurrencies 是数组
        const supportedCurrencies = Array.isArray(state.supportedCurrencies) ? state.supportedCurrencies : DEFAULT_SUPPORTED_CURRENCIES

        // Validate if saved currency is still in supported list
        // 验证保存的货币是否仍在支持列表中
        if (supportedCurrencies.length === 0 || supportedCurrencies.some(c => c.code === savedCurrency)) {
          commit('setCurrentCurrency', savedCurrency)
          return
        }
      }

      // 2. Use venue's default currency / 使用餐厅的默认货币
      commit('setCurrentCurrency', state.defaultCurrency || DEFAULT_CURRENCY)
    },

    /**
     * Change current currency (and save per-venue preference)
     * 切换货币（并保存按餐厅的偏好）
     *
     * @param {Object|string} payload - Currency code string or { currency, venueId }
     */
    changeCurrency({ commit, rootState }, payload) {
      // Support both old format (string) and new format (object)
      // 同时支持旧格式（字符串）和新格式（对象）
      let currency, venueId
      if (typeof payload === 'string') {
        currency = payload
        venueId = rootState.venueId
      } else {
        currency = payload.currency
        venueId = payload.venueId || rootState.venueId
      }

      commit('setCurrentCurrency', currency)
      // Save per-venue preference / 保存按餐厅的偏好
      saveLocalePrefs(venueId, 'currency', currency)
    },

    /**
     * Initialize currency settings from API data
     * 初始化货币配置（从 API 数据）
     *
     * API settings will be merged with default settings.
     * Then initializes currency based on per-venue preference or default.
     * API 设置将与默认设置合并。
     * 然后根据按餐厅的偏好或默认值来初始化货币。
     *
     * @param {Object} payload - { apiSettings, venueId } or just apiSettings
     */
    initCurrencySettings({ commit, dispatch }, payload) {
      // Support both old format (apiSettings only) and new format (object with venueId)
      // 同时支持旧格式（仅 apiSettings）和新格式（包含 venueId 的对象）
      let apiSettings, venueId
      if (payload && typeof payload === 'object' && ('apiSettings' in payload || 'venueId' in payload)) {
        apiSettings = payload.apiSettings
        venueId = payload.venueId
      } else {
        apiSettings = payload
        venueId = null
      }

      // Merge API settings with defaults / 将 API 设置与默认值合并
      const mergedSettings = mergeCurrencySettings(apiSettings)

      // Set default currency / 设置默认货币
      commit('setDefaultCurrency', mergedSettings.defaultCurrency)

      // Set supported currencies list / 设置支持的货币列表
      commit('setSupportedCurrencies', mergedSettings.supportedCurrencies)

      // Set exchange rates / 设置汇率
      commit('setExchangeRates', mergedSettings.exchangeRates)

      // Initialize currency (per-venue preference -> default)
      // 初始化货币（按餐厅偏好 -> 默认）
      dispatch('initCurrency', venueId)
    },
  },
}
